/**
 * Claude Status Indicator
 *
 * Badge-style status indicator showing Claude CLI authentication state.
 * Shows in the header with a tooltip. Clicking opens settings to the Claude tab.
 */

import { useState, useEffect, useCallback } from 'react';
import { api, type ClaudeAuthStatus } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/utils';

// ============================================
// Types
// ============================================

interface ClaudeStatusIndicatorProps {
  onClick?: () => void;
  className?: string;
}

type StatusType = 'loading' | 'connected' | 'disconnected' | 'error' | 'not-installed';

// ============================================
// ClaudeStatusIndicator Component
// ============================================

export function ClaudeStatusIndicator({ onClick, className }: ClaudeStatusIndicatorProps) {
  const { user } = useAuthStore();

  // Use claudeConnected from auth as initial state for instant feedback
  const [status, setStatus] = useState<ClaudeAuthStatus | null>(() =>
    user?.claudeConnected
      ? { cliInstalled: true, authenticated: true }
      : null
  );
  const [loading, setLoading] = useState(!user?.claudeConnected);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.claude.getStatus();
      setStatus(response);
    } catch (err) {
      console.error('Failed to fetch Claude status:', err);
      setStatus({
        cliInstalled: false,
        authenticated: false,
        error: 'Failed to check status',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch status on mount and periodically
  useEffect(() => {
    fetchStatus();

    // Refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);

    // Listen for auth status changes (dispatched from ClaudeAuthPanel)
    const handleAuthChange = () => {
      fetchStatus();
    };
    window.addEventListener('claude-auth-changed', handleAuthChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('claude-auth-changed', handleAuthChange);
    };
  }, [fetchStatus]);

  // Determine status type
  const getStatusType = (): StatusType => {
    if (loading) return 'loading';
    if (!status?.cliInstalled) return 'not-installed';
    if (status?.authenticated) return 'connected';  // Check authenticated BEFORE error
    if (status?.error) return 'error';
    return 'disconnected';
  };

  const statusType = getStatusType();

  // Status configurations - badge colors and tooltip
  const statusConfig: Record<StatusType, { tooltip: string; bgColor: string; textColor: string }> = {
    loading: {
      tooltip: 'Checking Claude status...',
      bgColor: 'bg-surface-200 dark:bg-surface-700',
      textColor: 'text-surface-500 dark:text-surface-400',
    },
    connected: {
      tooltip: status?.accountEmail
        ? `Connected as ${status.accountEmail}`
        : 'Claude is connected and ready',
      bgColor: 'bg-green-500/20 dark:bg-green-500/30',
      textColor: 'text-green-600 dark:text-green-400',
    },
    disconnected: {
      tooltip: 'Click to connect your Claude account',
      bgColor: 'bg-amber-500/20 dark:bg-amber-500/30',
      textColor: 'text-amber-600 dark:text-amber-400',
    },
    error: {
      tooltip: status?.error || 'Claude connection error',
      bgColor: 'bg-red-500/20 dark:bg-red-500/30',
      textColor: 'text-red-600 dark:text-red-400',
    },
    'not-installed': {
      tooltip: 'Claude CLI is not installed on the server',
      bgColor: 'bg-surface-200 dark:bg-surface-700',
      textColor: 'text-surface-500 dark:text-surface-400',
    },
  };

  const config = statusConfig[statusType];

  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 rounded text-[10px] font-semibold uppercase tracking-wide transition-all',
        config.bgColor,
        config.textColor,
        'hover:opacity-80',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-surface-900',
        className
      )}
      title={config.tooltip}
    >
      Claude
    </button>
  );
}

export default ClaudeStatusIndicator;
